import { useRef, useState, useCallback, useEffect } from 'react';
import { RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SlideCaptchaData } from '@/lib/api';

interface SlideCaptchaProps {
    captcha: SlideCaptchaData;
    onVerify: (pointX: number, pointY: number) => void;
    onRefresh: () => void;
    disabled?: boolean;
    /** 验证状态：idle / success / error */
    status?: 'idle' | 'success' | 'error';
}

const TRACK_HEIGHT = 36;
const BTN_SIZE = 36;
/** 图片区域最小高度，防止图片加载前塌缩 */
const IMG_MIN_HEIGHT = 140;

export function SlideCaptcha({ captcha, onVerify, onRefresh, disabled = false, status = 'idle' }: SlideCaptchaProps) {
    const [dragging, setDragging] = useState(false);
    const [offsetX, setOffsetX] = useState(0);
    const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const startXRef = useRef(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const bgImgRef = useRef<HTMLImageElement>(null);

    // 监听容器宽度
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const trackWidth = containerWidth || 280;
    const maxOffset = trackWidth - BTN_SIZE;

    const clamp = (val: number) => Math.max(0, Math.min(val, maxOffset));

    const toImageX = useCallback(
        (displayX: number): number => {
            const img = bgImgRef.current;
            if (!img || img.naturalWidth === 0) return displayX;
            const scale = img.naturalWidth / trackWidth;
            return Math.round(displayX * scale);
        },
        [trackWidth],
    );

    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            if (disabled || status === 'success') return;
            e.currentTarget.setPointerCapture(e.pointerId);
            setDragging(true);
            startXRef.current = e.clientX - offsetX;
        },
        [disabled, status, offsetX],
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (!dragging) return;
            const next = clamp(e.clientX - startXRef.current);
            setOffsetX(next);
        },
        [dragging, maxOffset],
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (!dragging) return;
            setDragging(false);
            const finalX = clamp(e.clientX - startXRef.current);
            setOffsetX(finalX);
            onVerify(toImageX(finalX), captcha.tileY);
        },
        [dragging, captcha.tileY, onVerify, maxOffset, toImageX],
    );

    useEffect(() => {
        setOffsetX(0);
        setNaturalSize(null);
    }, [captcha.captchaId]);

    const handleRefresh = () => {
        if (disabled) return;
        setOffsetX(0);
        onRefresh();
    };

    // 图片加载后，容器高度 = 图片实际渲染高度（宽度跟随容器，等比缩放）
    const rawImgHeight = naturalSize ? Math.round((naturalSize.h / naturalSize.w) * trackWidth) : IMG_MIN_HEIGHT;
    const imgDisplayHeight = rawImgHeight;
    const scaleX = naturalSize ? trackWidth / naturalSize.w : 1;
    // 滑块坐标和尺寸统一用 scaleX 等比缩放（图片底部可能被裁切）
    const displayTileY = captcha.tileY * scaleX;
    const displayTileSize = {
        width: captcha.tileWidth * scaleX,
        height: captcha.tileHeight * scaleX,
    };

    return (
        <div className="select-none w-full" ref={containerRef}>
            {/* 验证图片区域：宽度跟随容器，高度等比自适应且有上限 */}
            <div className="relative rounded-xl overflow-hidden border border-border/50" style={{ height: imgDisplayHeight, minHeight: IMG_MIN_HEIGHT }}>
                {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: 图片加载事件用于计算坐标 */}
                <img
                    alt="验证码背景"
                    className="absolute inset-0 w-full h-auto"
                    draggable={false}
                    onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                            setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
                        }
                    }}
                    ref={bgImgRef}
                    src={`data:image/png;base64,${captcha.masterImage}`}
                />
                <img
                    alt="滑块"
                    className="absolute object-contain pointer-events-none"
                    draggable={false}
                    src={`data:image/png;base64,${captcha.tileImage}`}
                    style={{
                        width: displayTileSize.width,
                        height: displayTileSize.height,
                        top: displayTileY,
                        left: offsetX,
                        transition: dragging ? 'none' : 'left 0.1s',
                    }}
                />
                <button
                    className="absolute top-2 right-2 p-1 rounded bg-black/30 hover:bg-black/50 text-white transition-colors disabled:opacity-50"
                    disabled={disabled}
                    onClick={handleRefresh}
                    title="刷新验证码"
                    type="button"
                >
                    <RefreshCw className="h-4 w-4" />
                </button>
                {status === 'success' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 animate-in fade-in duration-200">
                        <CheckCircle2 className="h-10 w-10 text-green-500" />
                    </div>
                )}
                {status === 'error' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-destructive/20 animate-in fade-in duration-200">
                        <XCircle className="h-10 w-10 text-destructive" />
                    </div>
                )}
            </div>

            {/* 滑动轨道 */}
            <div
                className={cn(
                    'relative mt-2 rounded-full border',
                    'bg-muted border-border/50',
                    status === 'success' && 'bg-green-500/10 border-green-500/40',
                    status === 'error' && 'bg-destructive/10 border-destructive/40',
                )}
                ref={trackRef}
                style={{ height: TRACK_HEIGHT }}
            >
                <div
                    className={cn(
                        'absolute inset-y-0 left-0 rounded-full transition-colors',
                        status === 'success' ? 'bg-green-500/30' : 'bg-primary/15',
                        status === 'error' && 'bg-destructive/15',
                    )}
                    style={{ width: offsetX + BTN_SIZE }}
                />
                <span className={cn('absolute inset-0 flex items-center justify-center text-xs text-muted-foreground pointer-events-none', offsetX > 20 && 'opacity-0')}>
                    {status === 'success' ? '验证通过' : '向右滑动完成验证'}
                </span>
                <div
                    className={cn(
                        'absolute top-0 bottom-0 flex items-center justify-center rounded-full',
                        'bg-background border border-border shadow-sm cursor-grab active:cursor-grabbing',
                        'transition-colors duration-150',
                        dragging && 'shadow-md',
                        status === 'success' && 'bg-green-500 border-green-500 text-white cursor-default',
                        status === 'error' && 'bg-destructive border-destructive text-white',
                        disabled && 'opacity-50 cursor-not-allowed',
                    )}
                    onPointerCancel={handlePointerUp}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    style={{
                        width: BTN_SIZE,
                        height: BTN_SIZE,
                        left: offsetX,
                        transition: dragging ? 'none' : 'left 0.1s',
                    }}
                >
                    {status === 'success' ? (
                        <CheckCircle2 className="h-5 w-5" />
                    ) : status === 'error' ? (
                        <XCircle className="h-5 w-5" />
                    ) : (
                        <span className="text-muted-foreground text-lg select-none">›</span>
                    )}
                </div>
            </div>
        </div>
    );
}
